import React, { useState, useMemo } from 'react';

export interface PieChartData {
  label: string;
  value: number;
  color?: string;
}

export interface PieChartAnswerState {
  selectedIndices: number[];
  isCorrect?: boolean;
  isSubmitted: boolean;
}

interface PieChartProps {
  data: PieChartData[];
  mode?: 'display' | 'interactive';
  maxSelections?: number;
  labelType?: 'label' | 'percentage' | 'both' | 'none';
  answerState?: PieChartAnswerState;
  correctIndices?: number[];
  onSelectionChange?: (indices: number[]) => void;
  size?: number;
}

const DEFAULT_COLORS = [
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#3b82f6', // blue
  '#0ea5e9', // sky
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f97316', // orange
];

const PieChart: React.FC<PieChartProps> = ({
  data,
  mode = 'display',
  maxSelections = 1,
  labelType = 'label',
  answerState = { selectedIndices: [], isSubmitted: false },
  correctIndices = [],
  onSelectionChange,
  size = 280,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const padding = 50;
  const radius = (size - padding * 2) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  const totalValue = useMemo(() => {
    return data.reduce((sum, d) => sum + d.value, 0);
  }, [data]);

  const isInteractiveAndNotSubmitted = mode === 'interactive' && !answerState.isSubmitted;

  const slices = useMemo(() => {
    if (totalValue === 0) return [];

    let currentAngle = -90; // Start from top

    return data.map((d, i) => {
      const percentage = (d.value / totalValue) * 100;
      const sweepAngle = (d.value / totalValue) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sweepAngle;
      const midAngle = startAngle + sweepAngle / 2;

      currentAngle = endAngle;

      const color = d.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
      const isSelected = answerState.selectedIndices.includes(i);
      const isCorrectAnswer = correctIndices.includes(i);

      // Calculate path
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);

      const largeArcFlag = sweepAngle > 180 ? 1 : 0;

      const pathD = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z',
      ].join(' ');

      // Label position
      const labelRadius = radius + 25;
      const midAngleRad = (midAngle * Math.PI) / 180;
      const labelX = centerX + labelRadius * Math.cos(midAngleRad);
      const labelY = centerY + labelRadius * Math.sin(midAngleRad);

      let labelAnchor: 'start' | 'middle' | 'end' = 'middle';
      if (midAngle > -60 && midAngle < 60) {
        labelAnchor = 'start';
      } else if (midAngle > 120 || midAngle < -120) {
        labelAnchor = 'end';
      }

      // Format label
      let label = d.label;
      if (labelType === 'percentage') {
        label = `${Math.round(percentage)}%`;
      } else if (labelType === 'both') {
        label = `${d.label} (${Math.round(percentage)}%)`;
      } else if (labelType === 'none') {
        label = '';
      }

      // Indicator position (center of slice)
      const indicatorRadius = radius * 0.6;
      const indicatorX = centerX + indicatorRadius * Math.cos(midAngleRad);
      const indicatorY = centerY + indicatorRadius * Math.sin(midAngleRad);

      return {
        index: i,
        label,
        percentage,
        color,
        pathD,
        isSelected,
        isCorrectAnswer,
        labelX,
        labelY,
        labelAnchor,
        indicatorX,
        indicatorY,
        midAngle,
      };
    });
  }, [data, totalValue, answerState, correctIndices, labelType, centerX, centerY, radius]);

  const handleSliceClick = (index: number) => {
    if (!isInteractiveAndNotSubmitted || !onSelectionChange) return;

    const currentSelections = [...answerState.selectedIndices];
    const selectionIndex = currentSelections.indexOf(index);

    if (selectionIndex > -1) {
      currentSelections.splice(selectionIndex, 1);
    } else {
      if (currentSelections.length >= maxSelections) {
        currentSelections.shift();
      }
      currentSelections.push(index);
    }

    onSelectionChange(currentSelections);
  };

  return (
    <div className="flex justify-center">
      <svg width={size} height={size} className="overflow-visible">
        {/* Slices */}
        {slices.map((slice) => {
          const isHovered = hoveredIndex === slice.index;
          const fillColor = answerState.isSubmitted && slice.isSelected
            ? (slice.isCorrectAnswer ? '#10b981' : '#ef4444')
            : slice.color;

          return (
            <g key={slice.index}>
              <path
                d={slice.pathD}
                fill={fillColor}
                stroke={slice.isSelected ? '#fff' : 'rgba(15, 23, 42, 0.5)'}
                strokeWidth={slice.isSelected ? 3 : 1}
                opacity={slice.isSelected || isHovered ? 1 : 0.85}
                className={`transition-all duration-200 ${isInteractiveAndNotSubmitted ? 'cursor-pointer' : ''}`}
                style={{
                  transform: isHovered && isInteractiveAndNotSubmitted
                    ? `translate(${Math.cos((slice.midAngle * Math.PI) / 180) * 5}px, ${Math.sin((slice.midAngle * Math.PI) / 180) * 5}px)`
                    : 'translate(0, 0)',
                }}
                onClick={() => handleSliceClick(slice.index)}
                onMouseEnter={() => isInteractiveAndNotSubmitted && setHoveredIndex(slice.index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />

              {/* Selection indicator */}
              {answerState.isSubmitted && slice.isSelected && (
                <g>
                  <circle
                    cx={slice.indicatorX}
                    cy={slice.indicatorY}
                    r={12}
                    fill={slice.isCorrectAnswer ? '#10b981' : '#ef4444'}
                  />
                  <text
                    x={slice.indicatorX}
                    y={slice.indicatorY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#fff"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    {slice.isCorrectAnswer ? '✓' : '✗'}
                  </text>
                </g>
              )}

              {/* Label */}
              {slice.label && (
                <text
                  x={slice.labelX}
                  y={slice.labelY}
                  textAnchor={slice.labelAnchor}
                  dominantBaseline="central"
                  fill={slice.isSelected ? '#fff' : '#94a3b8'}
                  fontSize="12"
                  fontWeight={slice.isSelected ? 'bold' : 'normal'}
                >
                  {slice.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default PieChart;
